import React, { useState, useMemo, useEffect } from 'react';
import { Drawer, Radio, Button, Typography, message, Modal } from 'antd';
import {
  DrawerContent,
  DrawerBody,
  FullWidthSpace,
  FieldSpacing,
  DayRadioGroup,
  FullWidthSelect,
  SubscribersTextArea,
  ErrorText,
  DrawerFooter,
} from './styled';
import { useCreateScheduleSavedAnalysis } from './hooks/useCreateScheduleSavedAnalysis';
import { useEditSchedule } from '../ManageSchedules/hooks/useEditSchedule';
import useGetScheduleList, { ScheduleListItem } from '../ManageSchedules/hooks/useGetScheduleList';
import { useAddScheduleSubscribers } from './hooks/useAddScheduleSubscribers';
import { checkIsInternalUser } from '@src/globalStore/profileStore';
import {
  Frequency,
  ScheduleAnalysisDrawerProps,
  ScheduleFormErrors,
  DayOfWeek,
  DAYS_OF_WEEK,
  generateTimeOptions,
  computeNextRun,
  isExactMatch,
  normalizeTimeToHHmm,
  normalizeWeekDay,
  capitalize,
  parseSubscriberEmails,
  hasSameAnalysisAndFrequency,
  MatchSignature,
} from './utils';

const { Text } = Typography;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ScheduleAnalysisDrawer: React.FC<ScheduleAnalysisDrawerProps> = ({
  open,
  onClose,
  analysisItem,
  mode = 'create',
  scheduleData,
  onRequestOpenManageSchedules,
}) => {
  const [frequency, setFrequency] = useState<Frequency>(
    mode === 'edit' && scheduleData ? scheduleData.frequency : 'daily',
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | null>(null);
  const [subscribersText, setSubscribersText] = useState('');
  const [isInternal, setIsInternal] = useState<boolean>(false);
  const [errors, setErrors] = useState<ScheduleFormErrors>({});

  const isInternalUser = checkIsInternalUser();

  const createScheduleMutation = useCreateScheduleSavedAnalysis();
  const editScheduleMutation = useEditSchedule();
  const addSubscribersMutation = useAddScheduleSubscribers();
  const {
    data: schedules = [],
    isFetching: isSchedulesFetching,
    isError: isSchedulesError,
  } = useGetScheduleList({ open });
  const timeOptions = useMemo(() => generateTimeOptions(), []);

  const isEditMode = mode === 'edit';

  // Prepopulate form state when editing
  useEffect(() => {
    if (isEditMode && scheduleData && open) {
      setFrequency(scheduleData.frequency);
      // run_time comes as "HH:MM:SS" from the DB; extract "HH:MM"
      const timeValue = scheduleData.run_time ? scheduleData.run_time.substring(0, 5) : null;
      setSelectedTime(timeValue);
      setSelectedDay(
        scheduleData.frequency === 'weekly' && scheduleData.week_day
          ? (scheduleData.week_day as DayOfWeek)
          : null,
      );
      setSubscribersText(
        scheduleData.subscribers?.length ? scheduleData.subscribers.join(', ') : '',
      );
      setIsInternal(scheduleData.is_internal ?? false);
      setErrors({});
    }
  }, [isEditMode, scheduleData, open]);

  const handleFrequencyChange = (value: Frequency) => {
    setFrequency(value);
    // Clear day selection when switching away from weekly
    if (value !== 'weekly') {
      setSelectedDay(null);
      if (errors.day) {
        setErrors((prev) => ({ ...prev, day: undefined }));
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: ScheduleFormErrors = {};

    if (!selectedTime) {
      newErrors.time = 'Please select a time';
    }

    if (frequency === 'weekly' && !selectedDay) {
      newErrors.day = 'Please select a day of the week';
    }

    // Validate subscribers
    const rawEmails = subscribersText.trim();
    if (rawEmails) {
      const emails = rawEmails
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);

      const invalidEmails = emails.filter((email) => !EMAIL_REGEX.test(email));
      if (invalidEmails.length > 0) {
        newErrors.subscribers = `Invalid email(s): ${invalidEmails.join(', ')}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetFormState = () => {
    setFrequency('daily');
    setSelectedTime(null);
    setSelectedDay(null);
    setSubscribersText('');
    setIsInternal(false);
    setErrors({});
  };

  const saveCurrentFlow = async (emails: string[]): Promise<boolean> => {
    try {
      if (isEditMode && scheduleData) {
        const editPayload = {
          schedule_id: scheduleData.schedule_id,
          frequency,
          time: selectedTime!,
          day: frequency === 'weekly' ? selectedDay : null,
          next_run: computeNextRun(
            frequency,
            selectedTime!,
            frequency === 'weekly' ? selectedDay : null,
          ),
          subscribers: emails,
          is_internal: isInternal,
        };

        await editScheduleMutation.mutateAsync(editPayload);
        return true;
      }

      if (!analysisItem) {
        return false;
      }

      const createPayload = {
        analysis_id: analysisItem.id,
        analysis_query: analysisItem.query,
        frequency,
        time: selectedTime!,
        day: frequency === 'weekly' ? selectedDay : null,
        next_run: computeNextRun(
          frequency,
          selectedTime!,
          frequency === 'weekly' ? selectedDay : null,
        ),
        subscribers: emails,
        is_internal: isInternal,
      };

      await createScheduleMutation.mutateAsync(createPayload);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const promptPartialMatchDecision = (
    partialMatches: ScheduleListItem[],
  ): Promise<'examine' | 'continue'> => {
    const representativeSchedule = [...partialMatches].sort(
      (a, b) => a.schedule_id - b.schedule_id,
    )[0];
    const representativeTime = normalizeTimeToHHmm(representativeSchedule.run_time) || '-';
    const representativeWeekDay =
      representativeSchedule.frequency === 'weekly'
        ? normalizeWeekDay(representativeSchedule.frequency, representativeSchedule.week_day) || '-'
        : '-';

    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Similar schedule(s) already exist',
        content: (
          <div>
            <div>{`Found ${partialMatches.length} schedule(s) with the same analysis and frequency.`}</div>
            <div style={{ marginTop: 8 }}>
              {`Example: ${capitalize(representativeSchedule.frequency)}, ${representativeTime}, ${representativeWeekDay}`}
            </div>
          </div>
        ),
        okText: 'Examine existing',
        cancelText: 'Continue with current',
        closable: false,
        maskClosable: false,
        keyboard: false,
        centered: true,
        onOk: () => resolve('examine'),
        onCancel: () => resolve('continue'),
      });
    });
  };

  const handleSave = async () => {
    if (!validate() || !selectedTime) {
      return;
    }

    const emails = parseSubscriberEmails(subscribersText);
    if (!analysisItem) {
      return;
    }

    if (isSchedulesFetching) {
      message.info('Checking existing schedules...');
      return;
    }

    const signature: MatchSignature = {
      analysisId: analysisItem.id,
      frequency,
      time: selectedTime,
      weekDay: normalizeWeekDay(frequency, frequency === 'weekly' ? selectedDay : null),
    };

    const candidateSchedules =
      isEditMode && scheduleData
        ? schedules.filter((schedule) => schedule.schedule_id !== scheduleData.schedule_id)
        : schedules;

    if (isSchedulesError) {
      message.warning('Could not verify existing schedules. Continuing with current save.');
      const isSaved = await saveCurrentFlow(emails);
      if (isSaved) {
        resetFormState();
        onClose();
      }
      return;
    }

    const exactMatches = candidateSchedules
      .filter((schedule) => isExactMatch(schedule, signature))
      .sort((a, b) => a.schedule_id - b.schedule_id);

    if (exactMatches.length > 0) {
      const targetSchedule = exactMatches[0];
      if (!emails.length) {
        message.info('Matching schedule already exists. No subscribers to add.');
        resetFormState();
        onClose();
        return;
      }

      try {
        await addSubscribersMutation.mutateAsync({
          schedule_id: targetSchedule.schedule_id,
          subscribers: emails,
        });

        resetFormState();
        onClose();
      } catch (_error) {
        // Error toast is handled in mutation onError.
      }
      return;
    }

    const partialMatches = candidateSchedules.filter(
      (schedule) =>
        hasSameAnalysisAndFrequency(schedule, signature) && !isExactMatch(schedule, signature),
    );

    if (partialMatches.length > 0) {
      const decision = await promptPartialMatchDecision(partialMatches);
      if (decision === 'examine') {
        const representativeSchedule = [...partialMatches].sort(
          (a, b) => a.schedule_id - b.schedule_id,
        )[0];
        onClose();
        onRequestOpenManageSchedules?.({ scheduleId: representativeSchedule.schedule_id });
        return;
      }
    }

    const isSaved = await saveCurrentFlow(emails);
    if (isSaved) {
      resetFormState();
      onClose();
    }
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const isSaving =
    createScheduleMutation.isLoading ||
    editScheduleMutation.isLoading ||
    addSubscribersMutation.isLoading;

  return (
    <Drawer
      title={isEditMode ? 'Edit Schedule' : 'Schedule Analysis'}
      placement="right"
      closable={true}
      onClose={handleClose}
      open={open}
      width={480}
      destroyOnHidden={true}
    >
      <DrawerContent>
        <DrawerBody>
          <FullWidthSpace direction="vertical" size="large">
            {/* Frequency */}
            <div>
              <Text strong>Frequency *</Text>
              <FieldSpacing>
                <Radio.Group
                  value={frequency}
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                >
                  <Radio.Button value="hourly">Hourly</Radio.Button>
                  <Radio.Button value="daily">Daily</Radio.Button>
                  <Radio.Button value="weekly">Weekly</Radio.Button>
                </Radio.Group>
              </FieldSpacing>
            </div>

            {/* Day Selector (Weekly only) */}
            {frequency === 'weekly' && (
              <div>
                <Text strong>Day of Week *</Text>
                <FieldSpacing>
                  <DayRadioGroup
                    value={selectedDay}
                    onChange={(e) => {
                      setSelectedDay(e.target.value);
                      if (errors.day) {
                        setErrors((prev) => ({ ...prev, day: undefined }));
                      }
                    }}
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <Radio key={day} value={day}>
                        {day}
                      </Radio>
                    ))}
                  </DayRadioGroup>
                </FieldSpacing>
                {errors.day && <ErrorText type="danger">{errors.day}</ErrorText>}
              </div>
            )}

            {/* Time Selector */}
            <div>
              <Text strong>Time *</Text>
              <FieldSpacing>
                <FullWidthSelect
                  placeholder="Select time"
                  value={selectedTime}
                  onChange={(value) => {
                    setSelectedTime(value as string);
                    if (errors.time) {
                      setErrors((prev) => ({ ...prev, time: undefined }));
                    }
                  }}
                  options={timeOptions}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string)?.startsWith(input) ?? false
                  }
                />
              </FieldSpacing>
              {errors.time && <ErrorText type="danger">{errors.time}</ErrorText>}
            </div>

            {/* Subscribers */}
            <div>
              <Text strong>Subscribers</Text>
              <SubscribersTextArea
                value={subscribersText}
                onChange={(e) => {
                  setSubscribersText(e.target.value);
                  if (errors.subscribers) {
                    setErrors((prev) => ({ ...prev, subscribers: undefined }));
                  }
                }}
                placeholder="Enter comma-separated email addresses"
                rows={3}
                status={errors.subscribers ? 'error' : ''}
              />
              {errors.subscribers && <ErrorText type="danger">{errors.subscribers}</ErrorText>}
            </div>

            {/* Internal usage toggle – visible only to Conviva internal users */}
            {isInternalUser && (
              <div>
                <Text strong>For Conviva Internal Usage</Text>
                <FieldSpacing>
                  <Radio.Group value={isInternal} onChange={(e) => setIsInternal(e.target.value)}>
                    <Radio.Button value={true}>Yes</Radio.Button>
                    <Radio.Button value={false}>No</Radio.Button>
                  </Radio.Group>
                </FieldSpacing>
              </div>
            )}
          </FullWidthSpace>
        </DrawerBody>

        {/* Save Button */}
        <DrawerFooter>
          <Button type="primary" onClick={handleSave} loading={isSaving} block>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ScheduleAnalysisDrawer;
